import React, { useEffect, useState } from 'react';

// Redux/Hooks/APIs/Utils
import { parameterApi } from '@/main/api';
import { resetReducerParameterTypeValue } from '@/main/store/parameterTypeValue';
import { ParameterTypesResponse } from '@/main/models/response/parameterTypesResponse';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { getParameters, useParameterReducer } from '@/main/store/parameter';
import { ParameterFilterRequest } from '@/main/models/request/parameterFilterRequest';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getTranslateByKey } from '@/main/services/utils';
import { checkParametersChange } from '@/main/components/shared/tabs/bodyshop/tabSettingRegister/utils';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';

// Components
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Button from '@/main/components/shared/button/button';
import RateSection from './rateSection/rateSection';
import SectionParameter from '@/main/components/shared/tabs/bodyshop/tabSettingRegister/sectionParameter/sectionParameter';

// Styles
import * as S from './styles';

interface ITabClaimSettings {
  guidCompany: string | null | undefined;
  guidBodyShop: string | null | undefined;
}

function TabClaimSettings({
  guidCompany,
  guidBodyShop
}: Readonly<ITabClaimSettings>) {
  const lowercaseValues = (parameterTypes: ParameterTypesResponse[]) => {
    return parameterTypes.map(data => ({
      ...data,
      parameters: data.parameters.map(parameter => {
        if (data.idParameterTypeValue === ParameterTypeEnum.Boolean) {
          return {
            ...parameter,
            value: parameter.value.toLowerCase()
          };
        } else {
          return {
            ...parameter
          };
        }
      })
    }));
  };

  // #region hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // #endregion

  // #region reducers
  const { parameters: parameterReducer } = useParameterReducer();
  // #endregion

  // #region states
  const [dataParameterTypes, setDataParameterTypes] = useState<
    ParameterTypesResponse[]
  >([]);
  const [dataUserParameters, setDataUserParameters] = useState<
    ParameterTypesResponse[]
  >(lowercaseValues(dataParameterTypes));
  const [dataParametersScreen, setDataParametersScreen] = useState<
    ParameterTypesResponse[]
  >(lowercaseValues(dataParameterTypes));
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  const [isLoadingSaveSettings, setIsLoadingSaveSettings] = useState<boolean>(
    false
  );

  // #endregion

  // #region handlers
  function handleBack() {
    navigate(-1);
  }

  async function handleSaveSettings() {
    setIsLoadingSaveSettings(true);
    let resultConfirm = null;
    let filesRequest = undefined;

    resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) {
      setIsLoadingSaveSettings(false);
      return;
    }

    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    const changeJustification = {
      guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
      reasonDescription: resultConfirm?.observation || null,
      filesUpload: filesRequest
    };

    const requestSaveParameters: { guid: string; value: string }[] = [];

    dataParametersScreen.forEach((x: any) => {
      x.parameters.map((y: any) => {
        return requestSaveParameters.push({
          guid: y.guid,
          value: y.value
        });
      });
    });

    try {
      await parameterApi.saveParameter({
        parameters: requestSaveParameters,
        changeJustification
      });

      toast.success(
        getTranslateByKey('information.parametersSavedSuccessfully')
      );
      dispatch(resetReducerParameterTypeValue());
      setDataParameterTypes(dataParametersScreen);
      setDataUserParameters(lowercaseValues(dataParametersScreen));
    } finally {
      setIsLoadingSaveSettings(false);
    }
  }
  // #endregion

  // #region effects
  useEffect(() => {
    if (parameterReducer.data) {
      setDataParameterTypes(parameterReducer.data);
      setDataUserParameters(lowercaseValues(parameterReducer.data));
      setDataParametersScreen(lowercaseValues(parameterReducer.data));
    }
  }, [parameterReducer.data]);

  useEffect(() => {
    if (guidCompany) {
      const request: ParameterFilterRequest = {
        guidCompany,
        applicationType: SystemApplicationEnum.Claims
      };

      dispatch(getParameters(request));
    }
  }, []);
  // #endregion

  // #region Render Constants
  const disabledButtonSave = !checkParametersChange(
    dataParametersScreen,
    dataUserParameters
  );
  // #endregion

  return (
    <S.Container>
      <S.ContainerSection>
        <S.HeaderText>
          {getTranslateByKey('information.registerInsurerParameters')}
        </S.HeaderText>

        <SectionParameter
          dataParameterAPI={dataParameterTypes}
          dataParametersScreen={dataParametersScreen}
          setDataParametersScreen={setDataParametersScreen}
        />
      </S.ContainerSection>
      <RateSection guidCompany={guidCompany} guidBodyShop={guidBodyShop} />

      <S.FooterButtons>
        <SecondaryButton
          type="button"
          onClick={handleBack}
          id="id-bodyshopClaimSettings-button-back">
          {getTranslateByKey('information.back')}
        </SecondaryButton>
        <Button
          id="id-bodyshopClaimSettings-button-save"
          type="submit"
          disabled={disabledButtonSave || isLoadingSaveSettings}
          isLoading={isLoadingSaveSettings}
          onClick={handleSaveSettings}>
          {getTranslateByKey('information.save')}
        </Button>
      </S.FooterButtons>
      <ModalConfirmationReason closeByOverlay={false} />
    </S.Container>
  );
}

export default TabClaimSettings;
